import { Column, Table } from '@tanstack/react-table';
import { Menu, Text, Group } from '@mantine/core';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

interface ShowHideColumnsMenuItems<TData extends Record<string, unknown>> {
	allColumns: Column<TData>[];
	isSubMenu?: boolean;
	table: Table<TData>;
}

export const ShowHideColumnsMenuItems = <TData extends Record<string, unknown>>({
	allColumns,
	customRenderer,
	table,
}: ShowHideColumnsMenuItems<TData> & {
	customRenderer?: (table: Table<TData>, allColumns?: Column<TData>[]) => JSX.Element;
}) => {
	if (customRenderer) {
		return customRenderer(table, allColumns);
	}

	const toggleableColumns = allColumns.filter((column) => {
		const columnDef = column.columnDef as ExtendedColumnDef<TData>;

		return (
			!columnDef.meta?.disableToggleVisibility &&
			!(column.id === 'select' || column.id === 'selection') &&
			!(column.id === 'actions' || column.id === 'row-actions')
		);
	});

	return (
		<>
			{toggleableColumns.map((column) => {
				const isVisible = column.getIsVisible();
				const columnDef = column.columnDef as ExtendedColumnDef<TData>;

				let label = column.id;
				if (typeof columnDef.header === 'string') {
					label = columnDef.header;
				} else if (columnDef.columnLabel) {
					label = columnDef.columnLabel;
				}

				return (
					<Menu.Item
						key={column.id}
						style={{ cursor: 'default', padding: '0.5rem 1rem' }}
					>
						<Group justify="apart" wrap="nowrap">
							<AdvancedSwitch
								selectedValue={isVisible}
								onChange={() => {
									table.setColumnVisibility((prev) => ({
										...prev,
										[column.id]: !isVisible,
									}));
								}}
								rightText={
									<Text size="sm" c="dark.8" style={{ flex: 1 }}>
										{label}
									</Text>
								}
								unselectedLetterSpacing="0"
							/>
						</Group>
					</Menu.Item>
				);
			})}
		</>
	);
};
